import { useState } from "react";
import { Loader2, Sparkles, Tag, PlusCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const URG = {
  alta: "bg-red-100 text-red-700",
  media: "bg-amber-100 text-amber-700",
  baja: "bg-navy/5 text-navy/50",
};

// Promociones con IA: recomienda promos para producto estancado (antigüedad en
// tienda por baja rotación) + alto stock, y permite crearlas con un clic.
export default function RetailPromoAI() {
  const [meses, setMeses] = useState(2);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [created, setCreated] = useState({});

  const recommend = async () => {
    setLoading(true); setData(null); setCreated({});
    try {
      const { data: res } = await api.get("/retail/promotions/ai-recommend", { params: { meses } });
      setData(res);
      if (!res.recomendaciones?.length) toast.info("Sin productos estancados con alto stock por ahora.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo recomendar"); } finally { setLoading(false); }
  };

  const create = async (rec, i) => {
    try {
      await api.post("/retail/promotions/ai-create", {
        nombre: rec.nombre || `Promoción ${rec.sku}`, tipo: rec.tipo || "% descuento",
        skus: rec.sku, descuento: Number(rec.descuento) || 0, justificacion: rec.justificacion,
      });
      setCreated((c) => ({ ...c, [i]: true }));
      toast.success("Promoción creada en RETAIL Studio → Promociones (borrador)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  const recs = data?.recomendaciones || [];

  return (
    <div data-testid="retail-promo-ai">
      <div className="card-surface p-4 mb-4">
        <div className="flex items-center gap-2 overline text-navy/40 mb-3"><Sparkles className="w-3.5 h-3.5" /> Recomendar promociones con IA</div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-navy/50 mb-1 block">Antigüedad en tienda (meses sin rotación)</label>
            <input type="number" min="0.5" step="0.5" value={meses} onChange={(e) => setMeses(Number(e.target.value))} className="field w-40" data-testid="promoai-meses" />
          </div>
          <button onClick={recommend} disabled={loading} className="btn btn-primary" data-testid="promoai-run">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Recomendar
          </button>
        </div>
        {data && (
          <p className="text-xs text-navy/50 mt-3">
            {data.resumen?.candidatos || 0} producto(s) estancado(s) · Valor de inventario en riesgo: <b>${(data.resumen?.valor_inventario || 0).toLocaleString("es-MX")}</b>
          </p>
        )}
      </div>

      {loading ? (
        <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : recs.length > 0 ? (
        <div className="grid gap-3 md:grid-cols-2">
          {recs.map((r, i) => (
            <div key={i} className="card-surface p-4" data-testid={`promoai-rec-${r.sku}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{r.descripcion || r.sku}</div>
                  <div className="text-[11px] text-navy/40">{r.sku} · {r.stock} pzs en stock · {r.vendidas} vendidas</div>
                </div>
                {r.urgencia && <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${URG[r.urgencia] || URG.baja}`}>{r.urgencia}</span>}
              </div>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1 text-sm font-medium bg-gradient-violet text-white rounded-full px-3 py-1"><Tag className="w-3.5 h-3.5" /> {r.tipo}</span>
                {Number(r.descuento) > 0 && <span className="text-sm text-navy/70">{Math.round(Number(r.descuento))}% desc.</span>}
              </div>
              {r.justificacion && <p className="text-xs text-navy/55 mb-3">{r.justificacion}</p>}
              <button onClick={() => create(r, i)} disabled={created[i]} className="btn btn-outline text-sm w-full" data-testid={`promoai-create-${r.sku}`}>
                {created[i] ? "Creada ✓" : <><PlusCircle className="w-4 h-4" /> Crear promoción</>}
              </button>
            </div>
          ))}
        </div>
      ) : data ? (
        <div className="card-surface p-8 text-center text-sm text-navy/40 flex flex-col items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-navy/25" />
          Sin productos estancados con alto stock para el umbral indicado. Prueba a subir los meses o registra más ventas en el PoS.
        </div>
      ) : null}
    </div>
  );
}
